import { countOf, fmtClaim, type GbElimination, type GbSeatView, type GbVoteReveal } from './types.js';

// ---------------------------------------------------------------------------
// Vote reveal + elimination reveal
// ---------------------------------------------------------------------------
// All votes surface at once with arrows to their targets, then the
// eliminated seat's true values sit beside what they claimed, marked
// honest or lie. This is the primary feedback loop for reading
// opponents — it is never skipped or compressed.

export function VoteReveal({
  reveal,
  seats
}: {
  reveal: GbVoteReveal;
  seats: GbSeatView[];
}) {
  const label = (seatId: string) =>
    seats.find((seat) => seat.seatId === seatId)?.label ?? seatId;

  return (
    <section className="flex flex-col gap-2 rounded-lg border border-ec-line bg-ec-panel p-3">
      <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-ec-face">
        Round {reveal.round} votes
      </h3>

      <ul className="flex flex-col gap-1">
        {Object.entries(reveal.votes).map(([voterId, targetId]) => (
          <li key={voterId} className="flex items-center gap-2 font-serif text-sm">
            <span className="text-ec-face">{label(voterId)}</span>
            <span aria-label="votes for" className="text-ec-brass">
              →
            </span>
            <span
              className={
                targetId === reveal.eliminatedSeatId ? 'text-ec-oxblood-2' : 'text-ec-bone'
              }
            >
              {label(targetId)}
            </span>
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap gap-2 border-t border-ec-line pt-2">
        {Object.entries(reveal.counts).map(([seatId, count]) => (
          <span
            key={seatId}
            className="rounded-full border border-ec-line px-2 font-display text-[0.6rem] uppercase tracking-wide text-ec-bone"
          >
            {label(seatId)} {count}
          </span>
        ))}
      </div>

      {reveal.tieBreak && (
        <div className="rounded border border-ec-brass/40 bg-ec-brass/5 p-2">
          <p className="font-display text-[0.6rem] uppercase tracking-wide text-ec-brass">
            Tie-break · {reveal.tieBreak.preset}
          </p>
          <ol className="mt-1 flex flex-col gap-1">
            {reveal.tieBreak.steps.map((step, index) => (
              <li key={`${step.rule}-${index}`} className="font-serif text-xs text-ec-bone">
                <span className="text-ec-bone-dim">{step.rule.replace(/_/g, ' ')}:</span>{' '}
                {step.detail}
              </li>
            ))}
          </ol>
        </div>
      )}

      <p className="font-serif text-sm text-ec-face">
        <strong className="text-ec-oxblood-2">{label(reveal.eliminatedSeatId)}</strong> is out.
      </p>
    </section>
  );
}

/**
 * The round-end reveal: EVERY seat's hidden balls, not just the seat
 * that went out. All the balls return to the pool between rounds so
 * there is nothing left to protect, and the honesty read on the players
 * still in the game is the whole point of the beat — the engine has
 * always sent all four, and only the UI was throwing three away.
 */
export function EliminationReveal({
  elimination,
  seats
}: {
  elimination: GbElimination;
  seats: GbSeatView[];
}) {
  const seatOf = (seatId: string) => seats.find((candidate) => candidate.seatId === seatId);
  // Older matches (and the reveal fixtures) may carry only the
  // eliminated seat; fall back rather than render nothing.
  const reveals =
    elimination.reveals?.length > 0
      ? elimination.reveals
      : [{ seatId: elimination.seatId, balls: elimination.balls }];

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-ec-line bg-ec-panel p-3">
      <header className="flex flex-wrap items-baseline gap-2">
        <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-ec-face">
          Everyone turns over
        </h3>
        <span className="font-display text-[0.6rem] uppercase tracking-wide text-ec-bone-dim">
          round {elimination.round} · all {reveals.length} hands
        </span>
      </header>

      {/* Two hands per row once there is room — four stacked blocks is
          most of a laptop screen for something everyone has to read
          inside one phase. */}
      <div className="grid gap-2 sm:grid-cols-2">
      {reveals.map((reveal) => {
        const seat = seatOf(reveal.seatId);
        const lies = reveal.balls.filter((ball) => !ball.honest).length;
        const wentOut = reveal.seatId === elimination.seatId;
        return (
          <div
            key={reveal.seatId}
            className={[
              'flex flex-col gap-1 rounded border p-2',
              wentOut
                ? 'border-ec-oxblood-2/60 bg-ec-oxblood-deep/10'
                : 'border-ec-line/60 bg-ec-panel/40'
            ].join(' ')}
          >
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="font-display text-xs font-semibold uppercase tracking-wide text-ec-face">
                {seat?.label ?? reveal.seatId}
              </span>
              {wentOut && (
                <span className="font-display text-[0.55rem] uppercase tracking-wide text-ec-oxblood-2">
                  eliminated
                </span>
              )}
              {seat?.isYou && (
                <span className="rounded-full border border-ec-brass/60 px-1.5 font-display text-[0.55rem] uppercase tracking-wide text-ec-brass">
                  you
                </span>
              )}
              <span
                className={`font-display text-[0.55rem] uppercase tracking-wide ${
                  lies === 0 ? 'text-ec-go' : 'text-ec-oxblood-2'
                }`}
              >
                {lies === 0
                  ? 'told the truth'
                  : `${countOf(lies, 'lie')} of ${reveal.balls.length}`}
              </span>
              {seat && (seat.honesty.verifiedTrue > 0 || seat.honesty.verifiedLied > 0) && (
                <span className="ml-auto font-display text-[0.55rem] uppercase tracking-wide text-ec-bone-dim">
                  all game{' '}
                  <span className="text-ec-go">{seat.honesty.verifiedTrue}T</span>
                  {' / '}
                  <span className="text-ec-oxblood-2">{seat.honesty.verifiedLied}L</span>
                </span>
              )}
            </div>

            <table className="w-full text-left font-serif text-xs">
              <thead>
                <tr className="font-display text-[0.55rem] uppercase tracking-wide text-ec-bone-dim">
                  <th className="pb-0.5 font-normal">Claimed</th>
                  <th className="pb-0.5 font-normal">Actually</th>
                  <th className="pb-0.5 text-right font-normal">Verdict</th>
                </tr>
              </thead>
              <tbody>
                {reveal.balls.map((ball) => (
                  <tr key={ball.ballId} className="border-t border-ec-line/40">
                    <td className="py-0.5 tabular-nums text-ec-bone">{fmtClaim(ball.claimed)}</td>
                    <td className="py-0.5 tabular-nums text-ec-face">{fmtClaim(ball.actual)}</td>
                    <td className="py-0.5 text-right">
                      <span
                        className={`font-display text-[0.55rem] uppercase tracking-wide ${
                          ball.honest ? 'text-ec-go' : 'text-ec-oxblood-2'
                        }`}
                      >
                        {ball.honest ? 'honest' : 'lie'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
      </div>
    </section>
  );
}
